import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { apiClient, FeedbackRating } from '../utils/api';
import ReactMarkdown from 'react-markdown';
import './AIAssistant.css';
import FeedbackModal from './FeedbackModal';
import { WELCOME_SCREEN_PROMPTS } from '../config/promptLibrary';
import CategorySpendingChart from './CategorySpendingChart';
import NetWorthChart from './NetWorthChart';
import CashFlowChart from './CashFlowChart';
import SpendingTrendChart from './SpendingTrendChart';
import InvestmentProjectionChart, { InvestmentProjectionData } from './InvestmentProjectionChart';
import { formatCategory } from '../utils/categories';
import { budgetStatusColor } from '../utils/statusColors';

// Fallback query length limit, used until /ai/quota reports the backend's
// actual (env-configurable) MAX_QUERY_CHARS.
const DEFAULT_MAX_QUERY_CHARS = 2000;

interface QuotaStatus {
  tier: string;
  used: number;
  limit: number;     // -1 = unlimited
  remaining: number; // -1 = unlimited
  resets_at: string;
  max_query_chars?: number; // backend's query-length validation limit
}

type ChartSeriesDatum = Record<string, string | number | null>;

interface CategorySpendingChartData {
  type: 'category_spending';
  series: ChartSeriesDatum[];
  nodes?: never;
  links?: never;
}

interface NetWorthChartData {
  type: 'net_worth';
  series: ChartSeriesDatum[];
  nodes?: never;
  links?: never;
}

interface CashFlowChartData {
  type: 'cash_flow';
  nodes: Array<{ id: string }>;
  links: Array<{ source: string; target: string; value: number }>;
  series?: never;
}

interface BudgetStatusChartData {
  type: 'budget_status';
  data: {
    month: string;
    categories: Array<{
      category_name: string;     // raw Plaid enum — processing key only, never render
      display_name?: string;     // friendly label e.g. "Food & Drink"
      monthly_limit: number;
      spent_this_month: number;
      remaining: number;
      percent_used: number;
      status: 'on_track' | 'warning' | 'over_budget';
      transaction_count: number;
      top_merchants: string[];
    }>;
    overall_limit: number;
    overall_spent: number;
    overall_remaining: number;
    overall_percent_used: number;
    overall_status: 'on_track' | 'warning' | 'over_budget';
    budget_count: number;
    unbudgeted_spending: number;
    unbudgeted_categories: string[];
  };
  series?: never;
  nodes?: never;
  links?: never;
}

interface SpendingTrendChartData {
  type: 'spending_trend';
  label: string;
  filter_type: 'merchant' | 'category';
  series: Array<{ date: string; amount: number; merchant: string; category: string }>;
  nodes?: never;
  links?: never;
}

interface TransactionListChartData {
  type: 'transaction_list';
  transactions: Array<{
    date: string;
    merchant: string;
    amount: number;            // Plaid convention: positive = expense, negative = income
    category: string;
    subcategory?: string | null;
    account?: string | null;   // source account name e.g. "Chase Checking"
  }>;
  count: number;
  period?: string;
  series?: never;
  nodes?: never;
  links?: never;
}

type AnomalySeverity = 'high' | 'medium' | 'low';

interface UnusualTransactionsChartData {
  type: 'unusual_transactions';
  flagged_transactions: Array<{
    date: string;
    merchant: string;
    amount: number;
    category: string;
    account?: string | null;
    anomaly_type: string;
    severity: AnomalySeverity;
    reason: string;
  }>;
  velocity_alerts: Array<{
    date: string;
    transaction_count: number;
    baseline_daily_avg: number;
    reason: string;
  }>;
  summary?: {
    total_analyzed: number;
    total_flagged: number;
    velocity_days: number;
    baseline_days_used: number;
    period: string;
  };
  series?: never;
  nodes?: never;
  links?: never;
}

interface InvestmentProjectionChartData extends InvestmentProjectionData {
  nodes?: never;
  links?: never;
}

type ChartData =
  | CategorySpendingChartData
  | NetWorthChartData
  | CashFlowChartData
  | BudgetStatusChartData
  | SpendingTrendChartData
  | TransactionListChartData
  | UnusualTransactionsChartData
  | InvestmentProjectionChartData;
interface MessageFeedbackState {
  rating: FeedbackRating;
  comment: string | null;
}

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  chart_data?: ChartData;
  feedback?: MessageFeedbackState;
  // Follow-up suggestions from the live response only — never restored from
  // history, so chips vanish on reload/session switch by design.
  suggestions?: string[];
}

// AI message ids are backend conversation_history PKs (numeric strings); ids
// minted client-side (fallbacks) can't anchor feedback, so the thumbs disable.
const hasDbId = (msg: Message) => /^\d+$/.test(msg.id);

interface AIAssistantProps {
  sessionId: number | null;
  sessionTitle: string;
  onSessionUpdated: () => void;
  onBudgetChanged?: () => void;
  onGoalsChanged?: () => void;
}

export interface AIAssistantHandle {
  /** Send a message to the agent as if the user typed it (e.g. from sidebar shortcuts). */
  sendMessage: (text: string) => void;
}

/**
 * Renders a compact budget status card inside the AI chat message bubble.
 *
 * Design goals:
 *  - Compact (fits inside a chat message without overflowing)
 *  - Color-coded (green/amber/red based on status)
 *  - Readable at a glance without scrolling
 *
 * This is rendered automatically when the AI calls the get_budget_status tool
 * and Gemini's response includes chart_data of type "budget_status".
 */
const BudgetStatusDisplay: React.FC<{ data: BudgetStatusChartData['data'] }> = ({ data }) => {
  const statusColor = budgetStatusColor;

  const statusEmoji = (s: string) => {
    if (s === 'on_track') return '✓';
    if (s === 'warning') return '⚠';
    if (s === 'over_budget') return '✗';
    return '';
  };

  return (
    <div style={{
      backgroundColor: 'rgba(0,0,0,0.04)',
      borderRadius: '10px',
      padding: '14px',
      marginTop: '10px',
      fontSize: '0.85rem',
    }}>
      {/* Header */}
      <div style={{ fontWeight: 700, marginBottom: '10px', fontSize: '0.9rem' }}>
        📊 Budget Status — {data.month}
      </div>

      {/* Overall bar */}
      <div style={{ marginBottom: '12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
          <span style={{ fontWeight: 600 }}>Overall</span>
          <span style={{ color: statusColor(data.overall_status), fontWeight: 600 }}>
            {statusEmoji(data.overall_status)}&nbsp;
            ${data.overall_spent.toFixed(2)} / ${data.overall_limit.toFixed(2)}
            &nbsp;({data.overall_percent_used.toFixed(1)}%)
          </span>
        </div>
        <div style={{ height: '6px', backgroundColor: 'rgba(0,0,0,0.1)', borderRadius: '3px', overflow: 'hidden' }}>
          <div style={{
            height: '100%',
            width: `${Math.min(data.overall_percent_used, 100)}%`,
            backgroundColor: statusColor(data.overall_status),
            borderRadius: '3px',
          }} />
        </div>
      </div>

      {/* Per-category rows */}
      {data.categories.map(cat => (
        <div key={cat.category_name} style={{ marginBottom: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
            <span style={{ fontWeight: 500 }}>{cat.display_name || formatCategory(cat.category_name)}</span>
            <span style={{ color: statusColor(cat.status), fontSize: '0.8rem' }}>
              {statusEmoji(cat.status)}&nbsp;
              ${cat.spent_this_month.toFixed(2)} / ${cat.monthly_limit.toFixed(2)}
              &nbsp;({cat.percent_used.toFixed(1)}%)
            </span>
          </div>
          <div style={{ height: '5px', backgroundColor: 'rgba(0,0,0,0.08)', borderRadius: '3px', overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              width: `${Math.min(cat.percent_used, 100)}%`,
              backgroundColor: statusColor(cat.status),
              borderRadius: '3px',
            }} />
          </div>
          {cat.top_merchants.length > 0 && (
            <div className="budget-cat-merchants" style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '2px' }}>
              {cat.top_merchants.join(' · ')}
            </div>
          )}
        </div>
      ))}

      {/* Unbudgeted footer */}
      {data.unbudgeted_spending > 0 && (
        <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.08)', fontSize: '0.78rem', color: '#94a3b8' }}>
          + ${data.unbudgeted_spending.toFixed(2)} unbudgeted
          {data.unbudgeted_categories.length > 0 && (
            <span> ({data.unbudgeted_categories.join(', ')})</span>
          )}
        </div>
      )}
    </div>
  );
};

// Icon + accent color per category, matched by keyword so both Plaid
// friendly labels ("Food & Drink") and subcategories ("Groceries") resolve.
const CATEGORY_VISUALS: Array<{ match: RegExp; icon: string; color: string }> = [
  { match: /grocer/i,                                  icon: '🛒', color: '#43e97b' },
  { match: /food|drink|restaurant|coffee|dining/i,     icon: '🍽️', color: '#fa709a' },
  { match: /rent|utilit|bill|electric|water|energy/i,  icon: '💡', color: '#f5576c' },
  { match: /entertain|stream|subscript|music|movie/i,  icon: '🎬', color: '#a18cd1' },
  { match: /travel|flight|hotel|vacation/i,            icon: '✈️', color: '#4facfe' },
  { match: /transport|gas|fuel|auto|ride|taxi|parking/i, icon: '🚗', color: '#fee140' },
  { match: /shop|merchandise|retail|clothing/i,        icon: '🛍️', color: '#764ba2' },
  { match: /health|medical|pharmac|fitness|gym/i,      icon: '🏥', color: '#4facfe' },
  { match: /income|payroll|deposit|salary/i,           icon: '💰', color: '#43e97b' },
  { match: /loan|credit|payment|bank|fee/i,            icon: '💳', color: '#667eea' },
];

const categoryVisual = (label: string) =>
  CATEGORY_VISUALS.find(v => v.match.test(label)) ?? { icon: '💵', color: '#667eea' };

// "2026-06-11" / ISO datetime → "Jun 11"; falls back to the raw string
const formatTxnDate = (raw: string): string => {
  const d = new Date(raw.length <= 10 ? `${raw}T00:00:00` : raw);
  return isNaN(d.getTime())
    ? raw
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const TXN_CARD_INITIAL_ROWS = 5;

// Plaid convention: positive = expense, negative = income/credit
const formatTxnAmount = (amount: number): string => {
  const abs = Math.abs(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return amount < 0 ? `+$${abs}` : `-$${abs}`;
};

/**
 * Renders transactions returned by the get_transactions tool as a card of
 * rows (icon · merchant · date · category · amount) inside the AI bubble,
 * replacing the model's previous markdown bullet lists. Shows the first 5
 * rows with a "View all N transactions" expander.
 */
const TransactionListDisplay: React.FC<{ data: TransactionListChartData }> = ({ data }) => {
  const [expanded, setExpanded] = useState(false);
  const transactions = data.transactions ?? [];
  const visible = expanded ? transactions : transactions.slice(0, TXN_CARD_INITIAL_ROWS);

  if (transactions.length === 0) return null;

  return (
    <div className="txn-card">
      <ul className="txn-card-list">
        {visible.map((tx, i) => {
          const label = formatCategory(tx.subcategory || tx.category);
          const { icon, color } = categoryVisual(`${tx.subcategory ?? ''} ${tx.category}`);
          return (
            <li key={`${tx.date}-${tx.merchant}-${i}`} className="txn-card-row">
              <span
                className="txn-card-icon"
                style={{ backgroundColor: `${color}22` }}
                aria-hidden="true"
              >
                {icon}
              </span>
              <span className="txn-card-info">
                <span className="txn-card-merchant">{tx.merchant}</span>
                <span className="txn-card-meta">
                  {formatTxnDate(tx.date)} · {label}
                </span>
              </span>
              <span className="txn-card-right">
                <span className={`txn-card-amount ${tx.amount < 0 ? 'credit' : 'debit'}`}>
                  {formatTxnAmount(tx.amount)}
                </span>
                {tx.account && <span className="txn-card-account">{tx.account}</span>}
              </span>
            </li>
          );
        })}
      </ul>
      {transactions.length > TXN_CARD_INITIAL_ROWS && (
        <button
          type="button"
          className="txn-card-toggle"
          onClick={() => setExpanded(prev => !prev)}
          aria-expanded={expanded}
        >
          {expanded
            ? 'Show fewer transactions'
            : `View all ${transactions.length} transactions`}
        </button>
      )}
    </div>
  );
};

const SEVERITY_STYLES: Record<AnomalySeverity, { color: string; bg: string; label: string }> = {
  high:   { color: '#f87171', bg: 'rgba(239, 68, 68, 0.14)',  label: 'High' },
  medium: { color: '#fbbf24', bg: 'rgba(245, 158, 11, 0.14)', label: 'Medium' },
  low:    { color: '#60a5fa', bg: 'rgba(59, 130, 246, 0.14)', label: 'Low' },
};

/**
 * Renders analyze_unusual_transactions results as a severity color-coded
 * card: each flagged transaction gets a red/amber/blue accent (high/medium/low)
 * with the anomaly reason inline, plus a velocity-alert footer. Replaces the
 * model's previous markdown anomaly list.
 */
const UnusualTransactionsDisplay: React.FC<{ data: UnusualTransactionsChartData }> = ({ data }) => {
  const [expanded, setExpanded] = useState(false);
  // On mobile the verbose reason is clamped and expanded per-row on tap, so the
  // list stays compact instead of each row spanning 4-5 lines.
  const [openReasons, setOpenReasons] = useState<Record<string, boolean>>({});
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;
  const flagged = data.flagged_transactions ?? [];
  const alerts = data.velocity_alerts ?? [];
  const visible = expanded ? flagged : flagged.slice(0, TXN_CARD_INITIAL_ROWS);

  if (flagged.length === 0 && alerts.length === 0) return null;

  return (
    <div className="txn-card">
      <ul className="txn-card-list">
        {visible.map((tx, i) => {
          const sev = SEVERITY_STYLES[tx.severity] ?? SEVERITY_STYLES.low;
          const hasReason = !!tx.reason;
          // Key the expand state by a stable per-transaction id, not the array
          // index, so it can't attach to the wrong row if the list reorders.
          const rowKey = `${tx.date}-${tx.merchant}-${tx.amount}`;
          const open = !!openReasons[rowKey];
          const toggle = () => setOpenReasons(prev => ({ ...prev, [rowKey]: !prev[rowKey] }));

          const amount = (
            <span className={`txn-card-amount ${tx.amount < 0 ? 'credit' : 'debit'}`}>
              {formatTxnAmount(tx.amount)}
            </span>
          );

          // Mobile: compact row with no icon — merchant gets the full width, and
          // category/account/reason live in the tap-to-expand area below.
          if (isMobile) {
            return (
              <li
                key={`${tx.date}-${tx.merchant}-${i}`}
                className={`anomaly-row anomaly-row--collapsible${open ? ' anomaly-row--open' : ''}`}
                style={{ borderLeftColor: sev.color }}
                role="button"
                tabIndex={0}
                aria-expanded={open}
                onClick={toggle}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
                }}
              >
                <div className="anomaly-row-head">
                  <span className="txn-card-info">
                    <span className="anomaly-merchant-name">{tx.merchant}</span>
                    <span className="txn-card-meta">{formatTxnDate(tx.date)}</span>
                  </span>
                  {amount}
                  <span className={`anomaly-chevron${open ? ' anomaly-chevron--open' : ''}`} aria-hidden="true">›</span>
                </div>
                {open && (
                  <div className="anomaly-details">
                    <div className="anomaly-detail">
                      <span className="anomaly-detail-label">Severity</span>
                      <span className="anomaly-detail-value" style={{ color: sev.color }}>{sev.label}</span>
                    </div>
                    <div className="anomaly-detail">
                      <span className="anomaly-detail-label">Category</span>
                      <span className="anomaly-detail-value">{formatCategory(tx.category)}</span>
                    </div>
                    {tx.account && (
                      <div className="anomaly-detail">
                        <span className="anomaly-detail-label">Account</span>
                        <span className="anomaly-detail-value">{tx.account}</span>
                      </div>
                    )}
                    {hasReason && <div className="anomaly-reason">{tx.reason}</div>}
                  </div>
                )}
              </li>
            );
          }

          // Desktop / laptop: unchanged — icon, inline meta, reason in the column
          return (
            <li
              key={`${tx.date}-${tx.merchant}-${i}`}
              className="txn-card-row anomaly-row"
              style={{ borderLeftColor: sev.color }}
            >
              <span
                className="txn-card-icon"
                style={{ backgroundColor: sev.bg, color: sev.color }}
                aria-hidden="true"
              >
                ⚠
              </span>
              <span className="txn-card-info">
                <span className="txn-card-merchant">
                  {tx.merchant}
                  <span className="anomaly-badge" style={{ color: sev.color, backgroundColor: sev.bg }}>
                    {sev.label}
                  </span>
                </span>
                <span className="txn-card-meta">
                  {formatTxnDate(tx.date)} · {formatCategory(tx.category)}
                </span>
                {hasReason && <span className="anomaly-reason">{tx.reason}</span>}
              </span>
              <span className="txn-card-right">
                {amount}
                {tx.account && <span className="txn-card-account">{tx.account}</span>}
              </span>
            </li>
          );
        })}
      </ul>
      {flagged.length > TXN_CARD_INITIAL_ROWS && (
        <button
          type="button"
          className="txn-card-toggle"
          onClick={() => setExpanded(prev => !prev)}
          aria-expanded={expanded}
        >
          {expanded
            ? 'Show fewer transactions'
            : `View all ${flagged.length} flagged transactions`}
        </button>
      )}
      {alerts.length > 0 && (
        <div className="anomaly-velocity">
          {alerts.map((a, i) => (
            <div key={`${a.date}-${i}`} className="anomaly-velocity-row">
              <span aria-hidden="true">📈</span>
              <span>{a.reason}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* Monochrome silhouette icons for the message action row. Solid fill inherits
   currentColor so all states (muted / hover / active) are pure CSS. */
const ThumbUpIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
    <path d="M1 21h4V9H1v12zM23 10c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z" />
  </svg>
);

const ThumbDownIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
    <path d="M15 3H6c-.83 0-1.54.5-1.84 1.22l-3.02 7.05c-.09.23-.14.47-.14.73v2c0 1.1.9 2 2 2h6.31l-.95 4.57-.03.32c0 .41.17.79.44 1.06L9.83 23l6.59-6.59c.36-.36.58-.86.58-1.41V5c0-1.1-.9-2-2-2zm4 0v12h4V3h-4z" />
  </svg>
);

const CopyIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
    <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
  </svg>
);

const RetryIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
    <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
  </svg>
);

/**
 * Action row under each AI message: rate the answer (opens the comment
 * modal), copy the raw markdown, or re-ask the question that produced it.
 */
const MessageActions: React.FC<{
  message: Message;
  feedbackEnabled: boolean;
  retryDisabled: boolean;
  copied: boolean;
  onFeedback: (msg: Message, rating: FeedbackRating) => void;
  onCopy: (msg: Message) => void;
  onRetry: (msg: Message) => void;
}> = ({ message, feedbackEnabled, retryDisabled, copied, onFeedback, onCopy, onRetry }) => (
  <div className="message-actions">
    <button
      className={`message-action-btn${message.feedback?.rating === 'up' ? ' active' : ''}`}
      onClick={() => onFeedback(message, 'up')}
      disabled={!feedbackEnabled}
      aria-pressed={message.feedback?.rating === 'up'}
      aria-label="Good response"
      title="Good response"
    >
      <ThumbUpIcon />
    </button>
    <button
      className={`message-action-btn${message.feedback?.rating === 'down' ? ' active' : ''}`}
      onClick={() => onFeedback(message, 'down')}
      disabled={!feedbackEnabled}
      aria-pressed={message.feedback?.rating === 'down'}
      aria-label="Bad response"
      title="Bad response"
    >
      <ThumbDownIcon />
    </button>
    <button
      className="message-action-btn"
      onClick={() => onCopy(message)}
      aria-label="Copy response"
      title="Copy response"
    >
      <CopyIcon />
    </button>
    <button
      className="message-action-btn"
      onClick={() => onRetry(message)}
      disabled={retryDisabled}
      aria-label="Retry question"
      title="Retry question"
    >
      <RetryIcon />
    </button>
    {copied && <span className="message-action-copied" role="status">Copied</span>}
  </div>
);

export const AIAssistant = forwardRef<AIAssistantHandle, AIAssistantProps>(({ sessionId, sessionTitle, onSessionUpdated, onBudgetChanged, onGoalsChanged }, ref) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quota, setQuota] = useState<QuotaStatus | null>(null);
  const [feedbackModal, setFeedbackModal] = useState<{ messageId: string; rating: FeedbackRating } | null>(null);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  // Mirror of feedbackModal for the rating PUT's catch handler, whose closure
  // would otherwise see the pre-click (stale) value.
  const feedbackModalRef = useRef<{ messageId: string; rating: FeedbackRating } | null>(null);
  // Per-message count of successful feedback saves. A slow rating PUT can fail
  // after a later save (comment submit / another thumb click) succeeded; the
  // count lets that stale failure detect it must not undo the newer save.
  const feedbackSaveCountRef = useRef<Record<string, number>>({});
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => () => {
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
  }, []);

  useEffect(() => {
    feedbackModalRef.current = feedbackModal;
  }, [feedbackModal]);

  // Daily quota is exhausted only for capped tiers (limit === -1 means unlimited)
  const limitReached = quota !== null && quota.limit !== -1 && quota.remaining <= 0;
  // Backend-reported query-length limit (single source of truth; fallback until loaded)
  const maxQueryChars = quota?.max_query_chars ?? DEFAULT_MAX_QUERY_CHARS;

  const refreshQuota = useCallback(async () => {
    try {
      const response = await apiClient.getQuota();
      setQuota(response.data);
    } catch {
      // Non-fatal: quota display is best-effort, never blocks the chat
    }
  }, []);
  const inputRef = useRef<HTMLInputElement>(null);
  // Tracks which session's history load is current; stale loads are discarded
  const currentSessionIdRef = useRef<number | null>(null);

  const scrollToBottom = () => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    messagesEndRef.current?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load the daily query quota once on mount so the UI can show usage / disable input
  useEffect(() => {
    refreshQuota();
  }, [refreshQuota]);

  // Re-focus the input after a query completes (runs after the re-render that
  // re-enables the disabled input — direct focus() in finally fires too early)
  useEffect(() => {
    if (!loading) {
      inputRef.current?.focus();
    }
  }, [loading]);

  // Load history whenever the active session changes; discard stale responses
  const loadHistory = useCallback(async (sid: number) => {
    try {
      const response = await apiClient.getConversationHistory(sid);
      if (currentSessionIdRef.current !== sid) return;
      if (response.data && Array.isArray(response.data)) {
        const historyMessages: Message[] = response.data.map((msg: any) => ({
          id: String(msg.id ?? Date.now()),
          text: msg.message,
          sender: msg.role === 'assistant' ? 'ai' : 'user' as 'user' | 'ai',
          timestamp: new Date(msg.created_at || new Date()),
          chart_data: msg.chart_data ?? undefined,
          feedback: msg.feedback ?? undefined,
        }));
        setMessages(historyMessages);
      }
    } catch (err) {
      if (currentSessionIdRef.current === sid) {
        console.error('Failed to load conversation history:', err);
      }
    }
  }, []);

  useEffect(() => {
    if (sessionId !== null) {
      currentSessionIdRef.current = sessionId;
      setMessages([]);
      loadHistory(sessionId);
      inputRef.current?.focus();
    } else {
      currentSessionIdRef.current = null;
      setMessages([]);
    }
  }, [sessionId, loadHistory]);

  // Shared AI query execution — used by both send handlers
  const executeAIQuery = useCallback(async (
    text: string,
    onSuccess: (aiMsg: Message) => void,
    retry: boolean = false,
  ) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.queryAI(text, sessionId!, retry);
      const aiMessage: Message = {
        id: response.data.message_id?.toString() || `local-${Date.now()}`,
        text: response.data.response,
        sender: 'ai',
        timestamp: new Date(),
        chart_data: response.data.chart_data ?? undefined,
        suggestions: response.data.suggestions?.length ? response.data.suggestions : undefined,
      };
      onSuccess(aiMessage);
      onSessionUpdated();
      refreshQuota();
      const toolsCalled: string[] = response.data.tools_called ?? [];
      if (onBudgetChanged && toolsCalled.some((t: string) =>
        ['set_budget', 'delete_budget', 'get_budget_status'].includes(t)
      )) {
        onBudgetChanged();
      }
      // Only refetch on writes — get_goal_status is read-only and changes nothing.
      if (onGoalsChanged && toolsCalled.some((t: string) =>
        ['create_goal', 'contribute_to_goal', 'delete_goal'].includes(t)
      )) {
        onGoalsChanged();
      }
    } catch (err: unknown) {
      const resp = (err as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
      const detail = resp?.data?.detail;
      // detail is a string for most errors, or {code, message} for the quota 429
      const detailMessage =
        typeof detail === 'string'
          ? detail
          : (detail as { message?: string })?.message;
      if (resp?.status === 429) {
        // Daily query limit reached — refresh quota so the input disables/updates
        setError(detailMessage || 'You have reached your daily query limit. Please try again tomorrow.');
        refreshQuota();
      } else if (resp?.status === 422) {
        setError(`Your message is too long. Please keep it under ${maxQueryChars} characters.`);
      } else {
        setError(detailMessage || 'Failed to get AI response. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [sessionId, onSessionUpdated, onBudgetChanged, onGoalsChanged, refreshQuota, maxQueryChars]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || sessionId === null) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: input,
      sender: 'user',
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);
    const sentText = input;
    setInput('');
    await executeAIQuery(sentText, (aiMsg) => setMessages(prev => [...prev, aiMsg]));
  };

  // Programmatic sends (sidebar shortcuts) follow the same flow as a typed
  // message — including the daily-quota guard, so shortcuts can't fire a
  // guaranteed-429 query after the cap is reached.
  useImperativeHandle(ref, () => ({
    sendMessage: (text: string) => {
      if (!text.trim() || sessionId === null || loading || limitReached) return;
      const userMessage: Message = {
        id: Date.now().toString(),
        text,
        sender: 'user',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, userMessage]);
      executeAIQuery(text, (aiMsg) => setMessages(prev => [...prev, aiMsg]));
    },
  }), [sessionId, loading, limitReached, executeAIQuery]);

  const handleDefaultQuestion = async (question: string) => {
    if (sessionId === null) return;
    const userMessage: Message = {
      id: Date.now().toString(),
      text: question,
      sender: 'user',
      timestamp: new Date(),
    };
    setMessages([userMessage]);
    await executeAIQuery(question, (aiMsg) => setMessages([userMessage, aiMsg]));
  };

  const handleSuggestionClick = (question: string) => {
    setInput(question);
    inputRef.current?.focus();
  };

  const setMessageFeedback = (id: string, feedback: MessageFeedbackState | undefined) => {
    setMessages(prev => prev.map(m => (m.id === id ? { ...m, feedback } : m)));
  };

  const bumpFeedbackSaveCount = (id: string) => {
    feedbackSaveCountRef.current[id] = (feedbackSaveCountRef.current[id] ?? 0) + 1;
  };

  // Thumb click: save the rating right away (so dismissing the modal loses
  // nothing), then open the modal for an optional written comment.
  const handleFeedbackClick = (msg: Message, rating: FeedbackRating) => {
    if (!hasDbId(msg)) return;
    const previous = msg.feedback;
    const saveCount = feedbackSaveCountRef.current[msg.id] ?? 0;
    setMessageFeedback(msg.id, { rating, comment: previous?.comment ?? null });
    setFeedbackError(null);
    setFeedbackModal({ messageId: msg.id, rating });
    apiClient.submitMessageFeedback(Number(msg.id), rating)
      .then(() => bumpFeedbackSaveCount(msg.id))
      .catch(() => {
        // A newer save already succeeded for this message — the server state
        // is current, so this stale failure must not undo anything.
        if ((feedbackSaveCountRef.current[msg.id] ?? 0) !== saveCount) return;
        setMessageFeedback(msg.id, previous);
        if (feedbackModalRef.current?.messageId === msg.id) {
          // Keep the modal (and any typed comment) open — Submit re-sends the
          // full rating+comment upsert, which retries this failed save.
          setFeedbackError('Failed to save your rating. Submit to try again.');
        } else {
          setError('Failed to save feedback. Please try again.');
        }
      });
  };

  const handleFeedbackSubmit = async (comment: string) => {
    if (!feedbackModal) return;
    setFeedbackSubmitting(true);
    setFeedbackError(null);
    try {
      // Full upsert — also self-heals if the rating-only PUT above failed
      await apiClient.submitMessageFeedback(Number(feedbackModal.messageId), feedbackModal.rating, comment);
      bumpFeedbackSaveCount(feedbackModal.messageId);
      setMessageFeedback(feedbackModal.messageId, {
        rating: feedbackModal.rating,
        comment: comment.trim() || null,
      });
      setFeedbackModal(null);
    } catch {
      // Keep the modal open so the typed comment isn't lost
      setFeedbackError('Failed to save your comment. Please try again.');
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const handleCopy = async (msg: Message) => {
    try {
      await navigator.clipboard.writeText(msg.text);
    } catch {
      // Clipboard API requires a secure context — fall back to the legacy path
      const textarea = document.createElement('textarea');
      textarea.value = msg.text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
      } finally {
        document.body.removeChild(textarea);
      }
    }
    setCopiedMessageId(msg.id);
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => setCopiedMessageId(null), 2000);
  };

  // Re-ask the question that produced this answer; the new exchange appends at
  // the bottom, mirroring a normal send (backend stores every query anyway).
  const handleRetry = (msg: Message) => {
    if (loading || sessionId === null || limitReached) return;
    const msgIndex = messages.findIndex(m => m.id === msg.id);
    let questionText: string | null = null;
    for (let i = msgIndex - 1; i >= 0; i--) {
      if (messages[i].sender === 'user') {
        questionText = messages[i].text;
        break;
      }
    }
    if (!questionText) {
      setError('Could not find the original question to retry.');
      return;
    }
    const userMessage: Message = {
      id: Date.now().toString(),
      text: questionText,
      sender: 'user',
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);
    executeAIQuery(questionText, (aiMsg) => setMessages(prev => [...prev, aiMsg]), true);
  };

  return (
    <div className="ai-assistant-container">
      <div className="ai-assistant">

        {/* Chat header — shows current session title */}
        <div className="chat-header-bar">
          <div className="chat-header-title">
            <span className="chat-header-icon" role="img" aria-label="Finn, your AI assistant">✨</span>
            <span className="chat-header-name" title={sessionTitle}>
              {sessionTitle || 'Finn'}
            </span>
          </div>
        </div>

        {error && (
          <div className="error-message" role="alert" aria-live="assertive">
            <span>{error}</span>
            <button onClick={() => setError(null)} aria-label="Close error message">✕</button>
          </div>
        )}

        {/* role="log" implies a polite live region scoped to the message list,
            so new messages are announced without the form/header chattering */}
        <div className="messages" role="log" aria-label="Chat messages" tabIndex={0}>
          {messages.length === 0 ? (
            <div className="welcome-message">
              <h3>Hi, I'm Finn — your financial assistant!</h3>
              <p>Click a question below to get started, or type your own:</p>
              <div className="default-questions">
                <span className="default-questions-label">✦ Suggested questions</span>
                {WELCOME_SCREEN_PROMPTS.map((q) => (
                  <button
                    key={q}
                    className="default-question-btn"
                    onClick={() => handleDefaultQuestion(q)}
                    disabled={loading}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map((msg, idx) => (
                <div key={msg.id} className={`message ${msg.sender}`}>
                  <div className={`message-content${msg.sender === 'ai' && msg.chart_data ? ' message-content--chart' : ''}`}>
                    {msg.sender === 'ai' ? (
                      <div lang="en">
                        <ReactMarkdown
                          components={{
                            // Remap heading levels so AI-generated h1/h2 don't
                            // override page-level landmarks; all visual styling
                            // lives in .message.ai .message-content CSS rules.
                            h1: ({ children }) => <h3>{children}</h3>,
                            h2: ({ children }) => <h4>{children}</h4>,
                            h3: ({ children }) => <h5>{children}</h5>,
                          }}
                        >
                          {msg.text}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      msg.text
                    )}
                    {msg.sender === 'ai' && msg.chart_data && (
                      <div className="chart-container">
                        {msg.chart_data.type === 'category_spending' && (
                          <CategorySpendingChart data={(msg.chart_data.series ?? []) as any} />
                        )}
                        {msg.chart_data.type === 'net_worth' && (
                          <NetWorthChart data={(msg.chart_data.series ?? []) as any} />
                        )}
                        {msg.chart_data.type === 'cash_flow' && (
                          <CashFlowChart
                            nodes={msg.chart_data.nodes ?? []}
                            links={msg.chart_data.links ?? []}
                          />
                        )}
                        {msg.chart_data.type === 'budget_status' && (
                          <BudgetStatusDisplay data={(msg.chart_data as BudgetStatusChartData).data} />
                        )}
                        {msg.chart_data.type === 'transaction_list' && (
                          <TransactionListDisplay data={msg.chart_data} />
                        )}
                        {msg.chart_data.type === 'unusual_transactions' && (
                          <UnusualTransactionsDisplay data={msg.chart_data} />
                        )}
                        {msg.chart_data.type === 'spending_trend' && (
                          <SpendingTrendChart
                            label={msg.chart_data.label}
                            filterType={msg.chart_data.filter_type}
                            series={msg.chart_data.series}
                          />
                        )}
                        {msg.chart_data.type === 'investment_projection' && (
                          <InvestmentProjectionChart data={msg.chart_data} />
                        )}
                      </div>
                    )}
                  </div>
                  {msg.sender === 'ai' && (
                    <MessageActions
                      message={msg}
                      feedbackEnabled={hasDbId(msg)}
                      retryDisabled={loading || limitReached || sessionId === null}
                      copied={copiedMessageId === msg.id}
                      onFeedback={handleFeedbackClick}
                      onCopy={handleCopy}
                      onRetry={handleRetry}
                    />
                  )}
                  {msg.sender === 'ai' &&
                    idx === messages.length - 1 &&
                    (msg.suggestions?.length ?? 0) > 0 && (
                    <div className="suggestion-chips" aria-label="Suggested follow-up questions">
                      {msg.suggestions!.map((q) => (
                        <button
                          key={q}
                          type="button"
                          className="suggestion-chip"
                          onClick={() => handleSuggestionClick(q)}
                          disabled={loading || limitReached || sessionId === null}
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="message-time">
                    {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="message ai ai-typing">
                  <div className="message-content">
                    <div className="typing-indicator" aria-hidden="true">
                      <span></span><span></span><span></span>
                    </div>
                    {/* Inside the role="log" live region, so insertion is announced once */}
                    <span className="sr-only">Assistant is typing…</span>
                  </div>
                </div>
              )}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>

        <form onSubmit={handleSendMessage} className="message-form">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            maxLength={maxQueryChars}
            placeholder={limitReached
              ? 'Daily query limit reached — resets tomorrow'
              : 'Ask me anything about your finances...'}
            aria-label="Message the AI assistant"
            disabled={loading || sessionId === null || limitReached}
          />
          <button
            type="submit"
            disabled={loading || !input.trim() || sessionId === null || limitReached}
            className="send-button"
            aria-label="Send message"
          >
            <span aria-hidden="true">{loading ? '...' : '→'}</span>
          </button>
        </form>
        {input.length > maxQueryChars * 0.8 && (
          <div className="ai-quota-meter">
            <span className="ai-char-count">{input.length} / {maxQueryChars}</span>
          </div>
        )}
        <p className="ai-disclaimer">
          This assistant uses AI to generate responses, which may occasionally be
          inaccurate or incomplete. Please double-check important information before making any financial decisions.
        </p>
      </div>

      {feedbackModal && (
        <FeedbackModal
          rating={feedbackModal.rating}
          initialComment={messages.find(m => m.id === feedbackModal.messageId)?.feedback?.comment ?? ''}
          submitting={feedbackSubmitting}
          error={feedbackError}
          onSubmit={handleFeedbackSubmit}
          onClose={() => setFeedbackModal(null)}
        />
      )}
    </div>
  );
});

AIAssistant.displayName = 'AIAssistant';
